import type { RouteStep, RouteTrack } from "@/app/queries";
import { StepMark } from "@/ui/StepMark";
import { STATE_MARK, type StepState } from "@/ui/route";
import { Eyebrow } from "./bits";
import { trackTone, type TrackTone } from "./chain";

/* S2 (2a): колонки треков слева направо, между ними «→»; «Алгоритмы» —
   отдельной пунктирной дорожкой снизу. Клик по шагу выбирает его. */

const headBorder: Record<TrackTone, string> = { done: "var(--accdim)", active: "var(--acc)", rest: "var(--bd)" };
const fracColor: Record<TrackTone, string> = { done: "text-ok", active: "text-acc", rest: "text-mut" };
const nameColor: Record<TrackTone, string> = { done: "text-ink", active: "text-ink", rest: "text-mut" };

// Кнопка шага: фон и рамка по состоянию (stepStyle в макете), цвет текста явно.
const stepLook: Record<StepState, string> = {
  done: "bg-oksoft border-accdim text-ink",
  active: "bg-accsoft border-acc text-ink",
  open: "bg-card border-bd2 text-ink",
  locked: "bg-transparent border-bd border-dashed text-mut",
};

function StepButton({ step, selected, onSelect, compact = false }: { step: RouteStep; selected: boolean; onSelect: (id: string) => void; compact?: boolean }) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={() => onSelect(step.id)}
      className={`grid w-full cursor-pointer items-center gap-2 rounded-sm border px-2 py-[7px] text-left font-sans text-[12.5px] leading-[1.35] ${stepLook[step.state]} ${compact ? "grid-cols-[20px_auto]" : "grid-cols-[20px_minmax(0,1fr)]"}`}
      style={{ outline: selected ? "2px solid var(--acc)" : "none", outlineOffset: 1 }}
    >
      <StepMark state={step.state} />
      <span className={compact ? "font-mono text-[11.5px]" : ""}>{compact ? `шаг ${step.position}` : step.title}</span>
    </button>
  );
}

export function TrackColumns({ tracks, selectedId, onSelect }: { tracks: RouteTrack[]; selectedId?: string; onSelect: (id: string) => void }) {
  return (
    <div className="flex flex-col gap-4 md:flex-row md:items-start md:gap-0">
      {tracks.map((t, i) => {
        const tone = trackTone(t);
        return (
          <div key={t.id} className="contents">
            <section data-testid="track-column" className="min-w-0 flex-1" aria-label={t.name}>
              <header className="mb-2.5 flex items-baseline justify-between gap-2 pb-2" style={{ borderBottom: `2px solid ${headBorder[tone]}` }}>
                <span className={`truncate font-mono text-[12px] font-semibold ${nameColor[tone]}`}>{t.name}</span>
                <span className={`font-mono text-[11px] ${fracColor[tone]}`}>
                  {t.done}/{t.total}
                </span>
              </header>
              <div className="flex flex-col gap-1.5">
                {t.steps.map((s) => (
                  <StepButton key={s.id} step={s} selected={s.id === selectedId} onSelect={onSelect} />
                ))}
              </div>
            </section>
            {i < tracks.length - 1 && (
              <span aria-hidden className={`hidden w-[34px] flex-none items-start justify-center pt-1 font-mono text-[14px] md:flex ${tone === "done" ? "text-accdim" : "text-bd2"}`}>
                →
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

/** Параллельный трек снизу: идёт одновременно с основной цепочкой, шаги никогда не закрыты. */
export function ParallelLane({ track, selectedId, onSelect }: { track: RouteTrack; selectedId?: string; onSelect: (id: string) => void }) {
  return (
    <section data-testid="parallel-lane" aria-label={track.name} className="mt-[26px] pt-5" style={{ borderTop: "1px dashed var(--bd2)" }}>
      <div className="mb-3 flex flex-wrap items-baseline gap-x-4 gap-y-1">
        <div>
          <Eyebrow>параллельный трек</Eyebrow>
          <div className="mt-1 font-mono text-[13px] font-semibold text-ink">{track.name}</div>
        </div>
        <p className="m-0 min-w-0 flex-1 text-[12.5px] text-ink2">идёт одновременно с ML, DL и NLP — примерно один шаг на один шаг основного маршрута</p>
        <span className="font-mono text-[11px] text-ink2">
          {track.done} / {track.total}
        </span>
      </div>
      <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-4 md:flex">
        {track.steps.map((s) => (
          <div key={s.id} className="min-w-0 md:flex-1" title={`${STATE_MARK[s.state].label} · ${s.title}`}>
            <StepButton step={s} selected={s.id === selectedId} onSelect={onSelect} compact />
          </div>
        ))}
      </div>
    </section>
  );
}
